/**
 * @fileoverview Input/output schemas for the chapter-metadata-extractor-v1 agent.
 *
 * Takes confirmed chapter boundaries + scoped text and extracts rich metadata:
 * keyEvents, chapterGoals, scenes, pacing, and POV character.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import {
  PacingEnum,
  ChapterGoalsSchema,
  CoreSceneSchema,
} from '@/domains/chapter-workspace/schemas/chapter-ideation';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  textInput: z.string().describe('A scoped text region containing the chapter to analyze, with some surrounding context.'),
  chapterNumber: z.number().describe('The chapter number (1-indexed).'),
  chapterHeading: z.string().describe('The exact chapter heading as confirmed by the user.'),
  title: z.string().describe('The chapter title as confirmed by the user.'),
  totalChapters: z.number().describe('Total number of chapters in the document.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  title: z.string().describe('The chapter title exactly as it appears in the source text. Record, do not invent — if the heading is just "1", "Chapter 1", or "PROLOGUE", that is the title. The numeric part may not match the chapter position (e.g. a prologue can offset numbering); preserve whatever the text says.'),
  povCharacterName: z.string().nullable().optional().describe("The name of the POV character, or null for omniscient narration."),
  keyEvents: z.string().describe(
    'A neutral summary of the key events in this chapter. Use objective style — no dialogue, no sensory details. Target ~1 paragraph per 1000 words of chapter content.'
  ),
  pacing: PacingEnum.describe('The pacing of the chapter.'),
  chapterGoals: ChapterGoalsSchema.describe('The narrative goals this chapter accomplishes.'),
  scenes: z.array(CoreSceneSchema).describe('The scenes within this chapter, in chronological order.'),
});

export type Output = z.infer<typeof OutputSchema>;
